/**
 * Hook: GET /api/t/{slug}/jds — JD list.
 *
 * Cache key: ``['jds', tenantSlug, statusFilter]``. 30c, 30d, 30e all consume.
 *
 * Response envelope: ``{ items, next_cursor, has_more }`` per
 * ``app/api/jds.py`` ``list_jds_endpoint``.
 *
 * Phase 2.3 (jd-rebuild-spec.md §7): supports a `statusFilter` arg.
 *   - omitted | undefined → backend default (excludes archived)
 *   - "all"               → returns every status
 *   - "active" | "draft" | "paused" | "archived" → narrowed
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import type { Jd } from "./useJd";

export type JdStatusFilter =
  | "active"
  | "draft"
  | "paused"
  | "archived"
  | "all";

interface JdsPage {
  items: Jd[];
  next_cursor: string | null;
  has_more: boolean;
}

interface UseJdsOptions {
  /** Optional status filter; omit for backend default (active+draft+paused). */
  statusFilter?: JdStatusFilter;
}

export function useJds(
  options: UseJdsOptions = {},
): UseQueryResult<Jd[], ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { statusFilter } = options;

  return useQuery<Jd[], ApiError>({
    queryKey: ["jds", tenantSlug, statusFilter ?? "_default"],
    queryFn: async () => {
      const url = statusFilter
        ? `/api/t/${tenantSlug}/jds?status=${encodeURIComponent(statusFilter)}`
        : `/api/t/${tenantSlug}/jds`;
      const r = await apiClient.get<JdsPage>(url);
      return r.data.items;
    },
    enabled: !!tenantSlug,
    staleTime: 30_000,
  });
}
